<style scoped lang="less">
@import "/styles/SiteColors.less";
.card-header {
    cursor: pointer;
    &:focus,
    &:hover {
        color: inherit;
        text-decoration: none;
    }
}
select.form-control,
input.form-control {
    font-weight: normal;
    color: @primary-lightest;
    border-color: @primary-lightest;

    &:focus {
        box-shadow: unset;
        outline: 2px solid @active-black;
    }
}

.search-group {
    label {
        font-size: 1.5em;
        color: #aaa;
        margin-bottom: 0;
        -webkit-transition: all 0.25s;
        transition: all 0.25s;
    }

    input#directorySearchBar {
        border-radius: 0.25em;
        /**
        * Remove default clear icon of search input type.
        * https://stackoverflow.com/a/77190241
        */
        appearance: textfield;
        outline-offset: -2px; // Fix outline style in Safari

        &::-webkit-search-decoration,
        &::-webkit-search-cancel-button,
        &::-webkit-search-results-button,
        &::-webkit-search-results-decoration {
            -webkit-appearance: none;
        }

        &:focus {
            border-bottom-width: 2px;
        }
    }

    .clear {
        position: absolute;
        right: 10px;
        z-index: 3;
        color: black;
        border: none;
        box-shadow: none;

        &:focus {
            outline-offset: 0;
            outline-color: @active-black;
        }
    }
}
.font-size-inherit {
    font-size: inherit;
}
.text-color-inherit {
    color: inherit;
}
</style>
<template>
    <div class="directory-container">
        <div class="container tile">
            <nav role="navigation" aria-label="Breadcrumbs">
                <ol class="breadcrumb">
                    <li class="breadcrumb-item"><a role="button" tabindex="0" @click="gotoIndex()">Home</a></li>
                    <li class="breadcrumb-item"><a href="" class="text-color-inherit" aria-current="page">Directory</a></li>
                </ol>
            </nav>
            <h1 class="">Directory</h1>
            <hr />
            <Promised :pending-delay="null" :promise="directoryDataPromise!">
                <!-- error slot -->
                <template v-slot:pending>
                    <LoadingWheel :width="100" />
                </template>
                <template v-slot:rejected="err"> Failed - {{ err }} </template>
                <template v-slot>
                    <div class="row" role="region" aria-label="Filters">
                        <div class="col-md-7 col-lg-6 col-xl-7 mb-2">
                            <ul class="pills" id="tags">
                                <li @click="toggleTag(tag)" @keydown.space.prevent="toggleTag(tag)" :class="{ active: isTagSelected(tag) }" v-for="tag in tags">
                                    <a tabindex="0" role="button" :class="{ disabled: !isTagEnabled(tag) }" :disabled="!isTagEnabled(tag)" :aria-disabled="!isTagEnabled(tag)" :aria-pressed="isTagEnabled(tag) ? isTagSelected(tag) : undefined">{{ tag }}</a>
                                </li>
                            </ul>
                            <a tabindex="0" role="button" v-show="selectedTags.length > 0" @click="deselectAllTags" @keydown.space.prevent="deselectAllTags" class="directory-deselect">Deselect all</a>
                        </div>
                        <div class="col-md-5 col-lg-6 col-xl-5">
                            <div id="directorySearchDiv">
                                <div class="form-row">
                                    <div class="col-lg-6 mb-2">
                                        <label class="sr-only" for="SortDirectory">Sort Directory</label>
                                        <select class="form-control" v-model="sort" id="SortDirectory">
                                            <option value="viewed">Sort by Most Viewed</option>
                                            <option value="alphabetical">Sort by A-Z</option>
                                        </select>
                                    </div>
                                    <div class="col-lg-6 mb-2 search-group input-group align-items-center">
                                        <label class="sr-only" for="directorySearchBar">Search the directory</label>
                                        <input ref="search" id="directorySearchBar" v-model="searchText" class="form-control search-control" placeholder="Search the directory..." />
                                        <button v-show="(searchText || '').length > 0" type="button" class="clear btn btn-bare icon icon-close px-1 py-0" aria-label="Clear search" @click.stop="clearSearch"></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div id="directoryAccordion" role="main" aria-label="Pages Listing">
                        <div class="accordion" role="tablist" aria-multiselectable="true">
                            <div class="card collapsible-card" v-for="page in sortedPagesWithGroupedVersion" :class="{ show: openPages[page.formName] }">
                                <h2 class="font-size-inherit my-0">
                                    <button class="card-header w-100 text-left border-0 py-3 mb-0" :aria-label="page.title" :aria-describedby="`${page.formName}_Description`" :aria-controls="`${page.formName}_MoreInfo`" :aria-expanded="openPages[page.formName]" @click.prevent="toggleOpenPage(page.formName)">
                                        <div class="pull-right float-right">
                                            <span class="icon" :class="['icon-caret-' + (openPages[page.formName] ? 'up' : 'down')]"></span>
                                        </div>
                                        {{ page.title }}
                                        <Promised :pending-delay="null" :promise="page.tenantDisplay" v-slot:combined="{ data: tenantDisplay }" v-if="page.tenant != rootTenantCode">
                                            &nbsp;
                                            <span class="badge badge-light">{{ tenantDisplay }}</span>
                                        </Promised>
                                    </button>
                                </h2>

                                <div class="collapse card-body" :class="{ show: openPages[page.formName] }" role="tabpanel" :id="`${page.formName}_MoreInfo`">
                                    <div class="row">
                                        <div class="col-sm-12" :class="[hasRelatedPages(page) || page.versions.length > 1 ? 'col-md-8' : null]">
                                            <div>
                                                <strong>Description</strong>
                                                <p :id="`${page.formName}_Description`">{{ page.description }}</p>
                                            </div>
                                            <div>
                                                <Promised :pending-delay="null" :promise="gotoUrl(page)" v-slot:combined="{ data: url }">
                                                    <a role="link" tabindex="0" @click.prevent="gotoPage(page)" :href="url" class="btn btn-primary" :aria-label="`Open ${page.title}`">Open</a>
                                                </Promised>
                                            </div>
                                        </div>
                                        <div class="col-md-4" v-show="hasRelatedPages(page) || page.versions.length > 1">
                                            <br class="d-block d-md-none" />
                                            <template v-if="page.versions.length > 1">
                                                <strong class="d-block mb-1">Versions</strong>
                                                <ul class="related_forms_list list text-smaller">
                                                    <li v-for="version in page.versions">
                                                        <Promised :pending-delay="null" :promise="gotoUrl(version)" v-slot:combined="{ data: url }">
                                                            <a @click.prevent="gotoPage(version)" :href="url">{{ version.versionLabel || version.title }}</a>
                                                        </Promised>
                                                    </li>
                                                </ul>
                                            </template>
                                            <template v-if="hasRelatedPages(page)">
                                                <strong class="d-block mb-1">Related</strong>
                                                <ul class="related_forms_list list text-smaller">
                                                    <li v-for="relatedPage in getRelatedPages(page)">
                                                        <Promised :pending-delay="null" :promise="gotoUrl(relatedPage)" v-slot:combined="{ data: url }">
                                                            <a @click.prevent="gotoPage(relatedPage)" :href="url">{{ relatedPage.title }}</a>
                                                        </Promised>
                                                    </li>
                                                </ul>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </Promised>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, inject, computed } from "vue";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import type { Directory } from "@cs/Directory";
import { Promised } from "@/lib/Promised";
import { useMainStore } from "../../mainStore";
import { changePageInjectKey, changePage } from "@/AppShell.3.vue.InjectKeys";
function convertPage(pageData: Directory["Pages"][0], tenantDisplay: Promise<string>) {
    return {
        title: pageData.FormTitle,
        versionLabel: pageData.FormVersionLabel,
        versionOrder: pageData.FormVersionOrder,
        versionPriority: pageData.FormVersionPriority,
        tenant: pageData.Tenant,
        tags: pageData.Tags,
        description: pageData.FormDescription,
        tenantDisplay: tenantDisplay,
        urlSlug: pageData.FormUrlSlug,
        formName: pageData.FormName,
        formVersion: pageData.FormVersion,
        formHash: pageData.FormHash,
        viewWeight: pageData.ViewWeight,
        relatedPages: pageData.RelatedPages?.map(p => ({ formTenant: p.Item1, formName: p.Item2, formVersion: p.Item3 })),
        openInNewTab: pageData.OpenInNewTab,
    };
}
type Page = ReturnType<typeof convertPage>;
export default defineComponent({
    components: {
        Promised,
        LoadingWheel,
    },
    setup() {
        const searchRef = ref<HTMLElement>(null!);
        const sort = ref("viewed");
        const directoryDataPromise = ref(null as null | Promise<{ pages: Page[]; tags: Directory["Tags"] }>);
        const pages = ref([] as Page[]);
        const openPages = ref({} as Record<string, boolean>);
        const tags = ref([] as Directory["Tags"]);
        const selectedTags = ref([] as Directory["Tags"]);
        const searchText = ref(null as string | null);

        const mainStore = useMainStore();
        const changePage = inject(changePageInjectKey)! as changePage;

        const visiblePagesWithGroupedVersion = computed(() => {
            const groups = {} as Record<string, Page & { versions: Page[] }>;
            const cmp = (a: string | undefined, b: string | undefined) => (b == null ? (a == null ? 0 : -1) : a == null ? 1 : a < b ? 1 : a > b ? -1 : 0);
            const versionPriorityCmp = (a: Page, b: Page) => (a.versionPriority != null || b.versionPriority != null ? cmp(a.versionPriority, b.versionPriority) : cmp(a.formVersion, b.formVersion));
            const versionOrderCmp = (a: Page, b: Page) => (a.versionOrder != null || b.versionOrder != null ? cmp(a.versionOrder, b.versionOrder) * -1 : a.versionLabel != null || b.versionLabel != null ? cmp(a.versionLabel, b.versionLabel) * -1 : cmp(a.formVersion, b.formVersion) * -1);
            visiblePages.value
                .slice()
                .sort(versionPriorityCmp)
                .forEach(p => {
                    const formName = p.formName;
                    if (!(formName in groups)) {
                        groups[formName] = { ...p, versions: [] };
                    }
                    groups[formName].versions.push(p);
                });
            const result = Object.values(groups);
            for (const item of result) {
                item.versions.sort(versionOrderCmp);
            }
            return result;
        });
        const visiblePages = computed(() => {
            const hasSearchText = searchText.value != null && !/^\s*$/.test(searchText.value);
            const searchTextI = hasSearchText ? searchText.value!.toLowerCase() : null;
            const hasTags = selectedTags.value.length > 0;
            const result = pages.value.filter(page => (!hasSearchText || (page.title ?? "").toLowerCase().indexOf(searchTextI!) >= 0) && (!hasTags || selectedTags.value.every(tag => page.tags.indexOf(tag) >= 0)));
            return result;
        });
        const sortedPagesWithGroupedVersion = computed(() => {
            var result = visiblePagesWithGroupedVersion.value.slice();
            const azSort = (a: Page, b: Page) => {
                var aKey = a.title ? a.title.toLowerCase() : null!;
                var bKey = b.title ? b.title.toLowerCase() : null!;
                return aKey < bKey ? -1 : aKey > bKey ? 1 : 0;
            };
            const viewSort = (a: Page, b: Page) => (a.viewWeight > b.viewWeight ? -1 : a.viewWeight < b.viewWeight ? 1 : azSort(a, b));
            result.sort(sort.value == "alphabetical" ? azSort : viewSort);
            return result;
        });

        const toggleOpenPage = (p: string) => {
            openPages.value[p] = !openPages.value[p];
        };

        const isTagSelected = (tag: string) => {
            return selectedTags.value.indexOf(tag) >= 0;
        };
        const hasRelatedPages = (page: Page) => {
            return getRelatedPages(page).length > 0;
        };
        const getRelatedPages = (page: Page) => {
            if (page) {
                return visiblePages.value.filter(p => (page.relatedPages ?? []).some(r => r.formName == p.formName && r.formVersion == p.formVersion));
            }
            return [];
        };
        const isTagEnabled = (tag: string) => {
            return visiblePages.value.some(page => page.tags.indexOf(tag) >= 0);
        };
        const toggleTag = (tag: string) => {
            if (!isTagEnabled(tag)) {
                return;
            }
            if (isTagSelected(tag)) {
                selectedTags.value = selectedTags.value.filter(item => tag != item);
            } else {
                selectedTags.value.push(tag);
            }
        };
        const deselectAllTags = () => {
            selectedTags.value = [];
            mainStore.setLiveRegion(Promise.resolve("All tags deselected."));
        };
        const gotoIndex = () => {
            changePage({ page: "Index" });
        };
        const gotoUrl = (page: Page) => {
            if (page.urlSlug) {
                return mainStore.getUrl("formGotoSlug", {
                    areaCode: page.tenant,
                    urlSlug: page.urlSlug,
                    page: 0,
                });
            } else {
                return mainStore.getUrl("formGoto", {
                    areaCode: page.tenant,
                    formName: page.formName,
                    formVersion: page.formVersion,
                    formHash: page.formHash,
                    page: 0,
                });
            }
        };
        const gotoPage = async (page: Page) => {
            //if (page.tenant == (await mainStore.tenantDataPromise).tenantCode) {
            changePage(
                {
                    page: "Page",
                    settings: {
                        urlSlug: page.urlSlug,
                        formName: page.formName,
                        formVersion: page.formVersion,
                        formHash: page.formHash,
                        page: 0,
                        areaCode: page.tenant,
                    },
                },
                false,
                false,
                !!page.openInNewTab,
            );
            //} else {
            //    gotoUrl.value(page).then(url => { window.location = url; });
            //}
        };
        const clearSearch = () => {
            searchText.value = null;
            searchRef.value?.focus();
        };

        directoryDataPromise.value = (async () => {
            const url = await mainStore.getUrl("getDirectoryData");
            const data = await fetch(url);
            const json: Directory = await data.json();
            const tenantDisplayLabels = mainStore.getTenantDisplayLabel(json.Pages.map(i => i.Tenant));
            const result = {
                pages: json.Pages.map(pageData => {
                    const tenantDisplay = tenantDisplayLabels[pageData.Tenant];
                    return convertPage(pageData, tenantDisplay);
                }),
                tags: json.Tags,
            };
            selectedTags.value = []; //TODO: Filter instead?
            tags.value = result.tags;
            pages.value = result.pages;
            return result;
        })();

        return {
            rootTenantCode: mainStore.tenantCode,
            search: searchRef,
            directoryDataPromise,
            selectedTags,
            searchText,
            openPages,
            tags,
            sort,
            sortedPagesWithGroupedVersion,
            toggleOpenPage,
            deselectAllTags,
            isTagEnabled,
            isTagSelected,
            toggleTag,
            hasRelatedPages,
            gotoPage,
            getRelatedPages,
            gotoUrl,
            gotoIndex,
            clearSearch,
        };
    },
});
</script>
